import { toast } from 'sonner';
import { Copy } from 'lucide-react';
import { initialsOf } from '@/components/projects/PeoplePicker';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/Avatar';
import { Label } from '@/components/ui/Label';
import type { SeatProfile } from '@/features/software/model';
import { cn } from '@/lib/utils';

export function PersonAvatar({
  id,
  name,
  profile,
  className,
}: {
  id: string;
  name: string;
  profile?: SeatProfile;
  className?: string;
}) {
  return (
    <Avatar className={cn('h-8 w-8', className)}>
      {profile?.avatar_url && <AvatarImage src={profile.avatar_url} alt="" />}
      <AvatarFallback className="text-[10px]">
        {initialsOf({ id, full_name: name, email: profile?.email ?? name })}
      </AvatarFallback>
    </Avatar>
  );
}

export function FormSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <div className="space-y-1">
        <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {title}
        </h3>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">{children}</div>
    </section>
  );
}

export function Field({
  label,
  htmlFor,
  hint,
  className,
  children,
}: {
  label: string;
  htmlFor?: string;
  hint?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 text-right text-foreground">{value}</span>
    </div>
  );
}

export function CopyButton({ value, label }: { value: string; label: string }) {
  return (
    <button
      type="button"
      className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
      aria-label={`Copy ${label}`}
      onClick={(event) => {
        event.stopPropagation();
        void navigator.clipboard
          .writeText(value)
          .then(() => toast.success(`${label} copied.`))
          .catch(() => toast.error('Could not copy.'));
      }}
    >
      <Copy className="h-3.5 w-3.5" />
    </button>
  );
}
